import {
  createContext,
  type FC,
  type ReactNode,
  useContext,
  useEffect,
} from 'react'
import { normalize } from '../utils/text.js'
import { parseQuery, serializeQuery } from '@saulx/utils'
import useGlobalState, { setGlobalState } from '../hooks/useGlobalState.js'

type SetFn = (newParams: Record<string, any>, reset?: boolean) => void
type SetLocation = (location: string, noHistory?: boolean) => boolean

let currentLocation = location.pathname + location.search
const KEY = '_location'

class Ctx {
  base: string
  location: string
  pathname: string
  query: Record<string, any>
  state: any = {}

  constructor(base: string) {
    this.base = base
    this.update()
  }

  update() {
    this.pathname = location.pathname.substring(this.base.length)
    this.location = this.pathname + location.search
    this.query =
      parseQuery(decodeURIComponent(location.search.substring(1))) || {}
  }

  cleanPath = () => {
    const cleanPath = this.base + this.pathname
    this.state.params = {
      section: this.state.params?.section ?? undefined,
      page: this.state.params?.page ?? undefined,
      id: this.state.params?.id ?? undefined,
    }

    history.replaceState(null, null, cleanPath)
  }

  setLocation = (loc: string) => {
    if (loc[0] !== '/') {
      if (loc[0] === '?') {
        loc = this.pathname + loc
      } else {
        loc = `/${loc}`
      }
    }

    if (this.location === loc) {
      return false
    }

    const fullLoc = this.base + loc
    const [pathname] = fullLoc.split('?')
    if (location.pathname !== pathname) {
      history.pushState(null, null, fullLoc)
    } else {
      history.replaceState(null, null, fullLoc)
    }

    updateAll()
  }

  setQuery = (string: Record<string, any>) => {
    if (string) {
      const query = searchToQuery(string)
      if (query) {
        return this.setLocation(`${this.pathname}?${query}`)
      }
    }
    return this.setLocation(this.pathname)
  }
}

const ctxs = new Map<string, Ctx>()
const getCtx = (base: string = '') => {
  let ctx = ctxs.get(base)

  if (ctx) {
    return ctx
  }
  ctx = new Ctx(base)
  ctxs.set(base, ctx)
  return ctx
}

const updateAll = () => {
  const newLocation = location.pathname + location.search

  if (newLocation !== currentLocation) {
    currentLocation = newLocation
    setGlobalState(KEY, currentLocation)
    for (const [, ctx] of ctxs) {
      ctx.update()
    }
  }
}

export const RouterCtx = createContext<Ctx>(getCtx())
export const Router: FC<{ base?: string; children: ReactNode }> = ({
  base = '',
  children,
}) => {
  return (
    <RouterCtx.Provider value={getCtx(base)}>{children}</RouterCtx.Provider>
  )
}

const searchToQuery = (search: Record<string, any>) => {
  for (const key in search) {
    if (search[key] === false || search[key] === null) {
      delete search[key]
    }
  }
  return serializeQuery(search)
}

let listeners = 0
export const useRouter = () => {
  const ctx = useContext(RouterCtx)
  if (listeners === 0) {
    ctx.update()
  }

  useEffect(() => {
    if (listeners === 0) {
      addEventListener('popstate', updateAll)
    }
    listeners++
    return () => {
      listeners--
      if (listeners === 0) {
        removeEventListener('popstate', updateAll)
      }
    }
  }, [])

  return ctx
}

export const useLocation = (): [location: string, setLocation: SetLocation] => {
  useGlobalState(KEY, currentLocation)
  const ctx = useRouter()
  return [ctx.location, ctx.setLocation]
}

export const useSearchParams = () => {
  useGlobalState(KEY, currentLocation)
  const ctx = useRouter()
  return [ctx.query, ctx.setQuery]
}

export const useCmsParams = (): [Record<string, any>, SetFn] => {
  useGlobalState(KEY, currentLocation)
  const ctx = useRouter()
  const { state, pathname, query } = ctx
  const [, section, page, id] = pathname.split('/')
  if (state.location !== location) {
    state.params = { ...query, section, page, id: id && Number(id) }
  }

  state.set ??= ((newParams, reset) => {
    const { section, page, id, ...rest } = reset
      ? newParams
      : { ...state.params, ...newParams }

    if (!Object.keys(newParams).length && reset === true) {
      ctx.cleanPath()
      return
    }

    const newSection = normalize(section)
    ctx.pathname = [newSection, normalize(page), normalize(id)]
      .filter(Boolean)
      .join('/')
    ctx.setQuery(rest)
  }) as SetFn

  return [state.params, state.set]
}
